import { useEffect, useState, useRef, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { CheckCircle, ArrowRight, FileText, Sparkles, Loader2, LogIn } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { paymentsApi } from '../services/api'

const POLL_INTERVAL_MS = 2000
const POLL_MAX_ATTEMPTS = 30 // 60 seconds max

export default function SubscriptionSuccessPage() {
  const [searchParams] = useSearchParams()
  const { isAuthenticated, isLoading: authLoading } = useAuth0()
  const orderId = searchParams.get('oid')
  const invoiceId = searchParams.get('invoice_id')
  const isMock = searchParams.get('mock') === 'true'
  const provider = searchParams.get('provider') // 'p24' or null (fakturownia)
  const isP24 = provider === 'p24'
  const queryClient = useQueryClient()
  const [isActivating, setIsActivating] = useState(false)
  const [activationError, setActivationError] = useState<string | null>(null)
  const [isActivated, setIsActivated] = useState(false)
  const mockEffectRan = useRef(false)
  const pollAbort = useRef<AbortController | null>(null)

  const invalidateAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['subscription'] })
    queryClient.invalidateQueries({ queryKey: ['subscription-current'] })
    queryClient.invalidateQueries({ queryKey: ['orders'] })
    queryClient.invalidateQueries({ queryKey: ['onboarding-status'] })
  }, [queryClient])

  // P24 real payment: poll order status independently of auth
  // Uses recursive setTimeout so the first check fires immediately
  useEffect(() => {
    if (isMock || !orderId) return

    const abort = new AbortController()
    pollAbort.current = abort
    let attempts = 0

    const poll = async () => {
      if (abort.signal.aborted) return
      attempts++

      if (attempts > POLL_MAX_ATTEMPTS) {
        // Timed out — show success anyway so user isn't stuck
        invalidateAll()
        setIsActivated(true)
        return
      }

      try {
        const response = await paymentsApi.getOrderStatus(orderId)
        if (abort.signal.aborted) return

        if (response.data.status === 'paid') {
          invalidateAll()
          setIsActivated(true)
          return
        }
      } catch {
        // Keep trying on error
      }

      if (!abort.signal.aborted) {
        setTimeout(poll, POLL_INTERVAL_MS)
      }
    }

    // Fire first check immediately
    poll()

    return () => {
      abort.abort()
      pollAbort.current = null
    }
  }, [orderId, isMock, invalidateAll])

  // Mock flows (need auth)
  useEffect(() => {
    if (authLoading) return
    if (mockEffectRan.current) return

    // P24 mock flow
    if (isP24 && isMock) {
      mockEffectRan.current = true
      setIsActivated(true)
      if (isAuthenticated) {
        invalidateAll()
      }
      return
    }

    // Fakturownia mock flow
    const confirmMockPayment = async () => {
      if (!isMock || isP24 || !orderId || !invoiceId) return
      if (!isAuthenticated) return

      mockEffectRan.current = true
      setIsActivating(true)
      setActivationError(null)

      try {
        await paymentsApi.confirmMockPayment({ orderId, invoiceId })
        setIsActivated(true)
        invalidateAll()
      } catch (error) {
        console.error('Failed to confirm mock payment:', error)
        setActivationError('Nie udalo sie aktywowac subskrypcji. Sprobuj ponownie.')
      } finally {
        setIsActivating(false)
      }
    }

    confirmMockPayment()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading])

  // Loading state while activating mock payment
  if (isActivating) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-md w-full text-center">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full">
              <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Aktywowanie subskrypcji...
          </h1>
          <p className="text-gray-600">
            Prosimy o chwile cierpliwosci.
          </p>
        </div>
      </div>
    )
  }

  // Show error state if activation failed
  if (activationError) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-md w-full text-center">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full">
              <span className="text-4xl">!</span>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Wystapil blad
          </h1>
          <p className="text-gray-600 mb-8">{activationError}</p>
          <a
            href="https://www.lextool.pl/cennik"
            className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            Powrot do cennika
          </a>
        </div>
      </div>
    )
  }

  // Waiting for P24 notification to activate subscription
  const isWaitingForActivation = !isMock && !isActivated && !!orderId

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-md w-full text-center">
        {/* Icon */}
        <div className="mb-6">
          <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${
            isWaitingForActivation ? 'bg-blue-100' : 'bg-green-100'
          }`}>
            {isWaitingForActivation ? (
              <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
            ) : (
              <CheckCircle className="w-12 h-12 text-green-600" />
            )}
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          {isWaitingForActivation
            ? 'Aktywowanie subskrypcji...'
            : 'Subskrypcja aktywowana!'}
        </h1>

        {/* Description */}
        <p className="text-lg text-gray-600 mb-8">
          {isWaitingForActivation
            ? 'Platnosc zostala przyjeta. Trwa aktywacja Twojej subskrypcji...'
            : isAuthenticated
              ? (isMock
                ? 'Twoja subskrypcja testowa zostala aktywowana. Mozesz teraz korzystac ze wszystkich funkcji planu.'
                : 'Dziekujemy za zakup. Twoja subskrypcja zostala aktywowana i mozesz korzystac ze wszystkich funkcji planu.')
              : 'Dziekujemy za zakup! Twoje konto zostalo utworzone. Zweryfikuj swoj adres email i zaloguj sie, aby rozpoczac korzystanie z LexTool.'}
        </p>

        {/* Order info */}
        {orderId && (
          <div className="bg-white rounded-lg shadow-sm p-4 mb-8">
            <p className="text-sm text-gray-500">
              Numer zamowienia: <span className="font-mono">{orderId.slice(0, 8)}...</span>
            </p>
            {isMock ? (
              <p className="text-sm text-orange-600 mt-1">
                [Tryb testowy - platnosc symulowana]
              </p>
            ) : (
              <p className="text-sm text-gray-500 mt-1">
                Faktura VAT zostanie wyslana na Twoj adres email.
              </p>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="space-y-3">
          {isAuthenticated ? (
            <>
              {isWaitingForActivation ? (
                <p className="text-sm text-gray-500">
                  Prosze czekac — aktywacja trwa zwykle kilka sekund.
                </p>
              ) : (
                <>
                  <Link
                    to="/"
                    onClick={() => invalidateAll()}
                    className="w-full inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Przejdz do aplikacji
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>

                  <div className="grid grid-cols-2 gap-3">
                    <Link
                      to="/generate"
                      className="inline-flex items-center justify-center px-4 py-2 bg-white text-gray-700 font-medium rounded-lg hover:bg-gray-50 shadow-sm transition-colors"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generuj opinie
                    </Link>
                    <Link
                      to="/documents"
                      className="inline-flex items-center justify-center px-4 py-2 bg-white text-gray-700 font-medium rounded-lg hover:bg-gray-50 shadow-sm transition-colors"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Dokumenty
                    </Link>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              {isWaitingForActivation ? (
                <p className="text-sm text-gray-500">
                  Prosze czekac — aktywacja trwa zwykle kilka sekund.
                </p>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="w-full inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    Zaloguj sie do LexTool
                  </Link>
                  <p className="text-sm text-gray-500">
                    Sprawdz skrzynke email - wyslalismy link weryfikacyjny.
                    Po weryfikacji mozesz sie zalogowac.
                  </p>
                </>
              )}
            </>
          )}
        </div>

        {/* Support info */}
        <p className="text-sm text-gray-500 mt-8">
          Masz pytania? Napisz do nas na{' '}
          <a href="mailto:support@lextool.pl" className="text-blue-600 hover:underline">
            support@lextool.pl
          </a>
        </p>
      </div>
    </div>
  )
}
